import { z } from "zod";

// After-sales vocabulary shared by the API, the seed, the management panel
// (reception, appointments, workshop, requests) and the customer panel.
// Pure module: keys, labels, zod shapes and tiny helpers only.

export const serviceTypeKeys = [
  "periodic",
  "oil",
  "mechanical",
  "body",
  "inspection",
  "software",
] as const;
export type ServiceTypeKey = (typeof serviceTypeKeys)[number];

export const serviceTypeLabels = {
  periodic: "سرویس دوره‌ای",
  oil: "تعویض روغن و فیلتر",
  mechanical: "تعمیرات مکانیکی",
  body: "خدمات بدنه و رنگ",
  inspection: "بازدید فنی و کارشناسی",
  software: "به‌روزرسانی نرم‌افزار",
} satisfies Record<ServiceTypeKey, string>;

export const appointmentStatusKeys = [
  "scheduled",
  "arrived",
  "done",
  "cancelled",
  "no_show",
] as const;
export type AppointmentStatusKey = (typeof appointmentStatusKeys)[number];

export const appointmentStatusLabels = {
  scheduled: "رزرو شده",
  arrived: "مراجعه کرده",
  done: "انجام شده",
  cancelled: "لغو شده",
  no_show: "عدم مراجعه",
} satisfies Record<AppointmentStatusKey, string>;

/** Appointments the customer still sees as upcoming */
export const openAppointmentStatuses = ["scheduled", "arrived"] as const;

/**
 * A booking still ahead of the customer. Read-time rule (no job rewrites rows): a «scheduled»
 * booking whose Tehran day has passed is a missed visit, so it is not «next» and does not
 * block a new booking; staff may still mark it «no_show» for the record.
 */
export const isOpenAppointment = (status: string, day: Date | string, now = new Date()) =>
  status === "arrived" || (status === "scheduled" && tehranDay(new Date(day)) >= tehranDay(now));

/** The status the customer reads: a past «scheduled» booking is shown as a no-show */
export const customerAppointmentStatus = (
  status: string,
  day: Date | string,
  now = new Date(),
): string => (status === "scheduled" && !isOpenAppointment(status, day, now) ? "no_show" : status);

/** How long one service visit occupies the reception desk */
export const SLOT_STEP_MINUTES = 90;

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

/** 570 -> «۰۹:۳۰»: minutes after midnight in the form the customer sees */
export const clockLabel = (minutes: number) => {
  const clamped = Math.max(0, Math.min(24 * 60, Math.round(minutes)));
  const text = `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
  return text.replace(/\d/g, (digit) => FA_DIGITS[Number(digit)] ?? digit);
};

/** «۰۹:۳۰» -> 570; NaN-safe for text that is not a clock at all */
export const clockMinutes = (label: string) => {
  const latin = label.replace(/[۰-۹]/g, (digit) => String(FA_DIGITS.indexOf(digit)));
  const [hours = "", rest = ""] = latin.split(":");
  const minutes = Number(hours) * 60 + Number(rest);
  return Number.isFinite(minutes) ? minutes : 0;
};

/** Statuses that hold a place in a branch's day; a cancelled or missed booking gives it back */
export const capacityAppointmentStatuses = ["scheduled", "arrived", "done"] as const;

/** Visits one arrival time takes: the branch's daily capacity spread evenly over its slots */
export const slotCapacity = (dailyCapacity: number, slotCount: number) =>
  slotCount === 0 ? 0 : Math.ceil(dailyCapacity / slotCount);

/**
 * Places a club tier with booking priority may take beyond a full slot or day.
 * ponytail: a fixed overbook of one; a reserved share of the day if branches ask for more
 */
export const PRIORITY_EXTRA_PLACES = 1;

/**
 * Places still free at each slot of a day; `held` has one entry (its slot) per live booking.
 * `extra` widens both the slot and the day for a priority customer.
 */
export const slotsLeft = (slots: string[], held: string[], dailyCapacity: number, extra = 0) => {
  // a branch that takes no bookings (capacity 0) takes none from priority customers either
  const bonus = dailyCapacity > 0 ? extra : 0;
  const perSlot = slotCapacity(dailyCapacity, slots.length) + bonus;
  const dayLeft = Math.max(0, dailyCapacity + bonus - held.length);
  return slots.map((slot) => ({
    slot,
    left: Math.min(dayLeft, Math.max(0, perSlot - held.filter((item) => item === slot).length)),
  }));
};

/** Why one more booking at `slot` does not fit: the day is full, or that slot is */
export const capacityProblem = (
  slots: string[],
  held: string[],
  dailyCapacity: number,
  slot: string,
  extra = 0,
): "day" | "slot" | null => {
  if (dailyCapacity === 0 || held.length >= dailyCapacity + extra) return "day";
  const atSlot = held.filter((item) => item === slot).length;
  return atSlot >= slotCapacity(dailyCapacity, slots.length) + extra ? "slot" : null;
};

/** The moment a booking starts: its Tehran day (stored at midnight) plus the slot's clock time */
export const appointmentStart = (scheduledAt: Date, slot: string) =>
  new Date(scheduledAt.getTime() + clockMinutes(slot) * 60_000);

/** The customer may move a booking from the panel until this many hours before it starts */
export const RESCHEDULE_CUTOFF_HOURS = 2;

export const hoursLabel = (open: number, close: number) =>
  `${clockLabel(open)} تا ${clockLabel(close)}`;

/**
 * Bookable arrival times of one working day at a branch: every SLOT_STEP_MINUTES
 * from the moment the service bay opens, while a whole visit still fits before it
 * closes. An empty window (or one shorter than a visit) means nothing is bookable.
 */
export const slotsBetween = (open: number, close: number) => {
  const slots: string[] = [];
  for (let at = open; at + SLOT_STEP_MINUTES <= close; at += SLOT_STEP_MINUTES) {
    slots.push(clockLabel(at));
  }
  return slots;
};

export const workOrderStatusKeys = [
  "admitted",
  "awaiting_review",
  "awaiting_customer",
  "awaiting_part",
  "in_repair",
  "repair_done",
  "ready",
  "declined",
  "delivered",
] as const;
export type WorkOrderStatusKey = (typeof workOrderStatusKeys)[number];

export const workOrderStatusLabels = {
  admitted: "پذیرش شده",
  awaiting_review: "در انتظار بررسی",
  awaiting_customer: "در انتظار تأیید مشتری",
  awaiting_part: "در انتظار قطعه",
  in_repair: "در حال تعمیر",
  repair_done: "تکمیل تعمیر",
  ready: "آماده تحویل",
  declined: "انصراف مشتری",
  delivered: "تحویل شده",
} satisfies Record<WorkOrderStatusKey, string>;

/**
 * Delivery closes the order and issues its invoice: a finished repair, or a vehicle whose
 * owner declined the repair (its invoice holds only what was done, e.g. the diagnosis)
 */
export const deliverableStatuses = [
  "repair_done",
  "ready",
  "declined",
] as const satisfies readonly WorkOrderStatusKey[];

export const canDeliver = (status: string) => deliverableStatuses.some((key) => key === status);

/**
 * Where staff may move an order from each status (workOrders.setStatus enforces it).
 * «awaiting_customer» is entered only by sending an estimate and left by its decision;
 * «delivered» is final: a mistake on its invoice is fixed with an amendment, never a reopen.
 */
export const workOrderTransitions = {
  admitted: ["awaiting_review", "awaiting_part", "in_repair", "declined"],
  awaiting_review: ["admitted", "awaiting_part", "in_repair", "declined"],
  awaiting_customer: ["awaiting_review", "awaiting_part", "in_repair", "declined"],
  awaiting_part: ["awaiting_review", "in_repair", "declined"],
  in_repair: ["awaiting_review", "awaiting_part", "repair_done", "declined"],
  repair_done: ["in_repair", "ready", "delivered"],
  ready: ["in_repair", "delivered"],
  declined: ["in_repair", "delivered"],
  delivered: [],
} as const satisfies Record<WorkOrderStatusKey, readonly WorkOrderStatusKey[]>;

export const workOrderCan = (from: WorkOrderStatusKey, to: WorkOrderStatusKey) =>
  workOrderTransitions[from].some((next) => next === to);

/** What the server knows about an order when it decides a move */
export type WorkOrderFacts = {
  technicianId: string | null;
  /** operations not ticked yet */
  openOperations: number;
};

/**
 * Why the order may not move from `from` to `to` right now; "" = it may. A finished repair
 * needs its technician and every operation ticked. Shared by the API (the rule) and the panel
 * (disabled steps, hints).
 */
export function workOrderMoveProblem(
  from: WorkOrderStatusKey,
  to: WorkOrderStatusKey,
  facts: WorkOrderFacts,
): string {
  if (to === "awaiting_customer") return "برای انتظار تأیید مشتری، یک برآورد هزینه ثبت کنید.";
  if (!workOrderCan(from, to)) {
    return `از «${workOrderStatusLabels[from]}» نمی‌توان به «${workOrderStatusLabels[to]}» رفت.`;
  }
  if (to === "repair_done") {
    if (!facts.technicianId) return "اول تکنسین مسئول را تعیین کنید.";
    if (facts.openOperations > 0) return "همه عملیات شرح کار باید تیک خورده باشند.";
  }
  return "";
}

/** Workshop decisions only a manager takes: invoice amendments */
export const managesWorkshop = (role: string) =>
  role === "service_manager" || role === "superadmin";

/** A vehicle back within this many days of a delivery is a comeback of that job */
export const COMEBACK_DAYS = 30;

// ---------------------------------------------------------------------------
// Admission (intake) record: what the vehicle arrived with, agreed with the customer
// ---------------------------------------------------------------------------

export const fuelLevelKeys = ["empty", "quarter", "half", "three_quarters", "full"] as const;
export type FuelLevelKey = (typeof fuelLevelKeys)[number];

export const fuelLevelLabels = {
  empty: "رزرو (تقریباً خالی)",
  quarter: "یک‌چهارم",
  half: "نیم",
  three_quarters: "سه‌چهارم",
  full: "پر",
} satisfies Record<FuelLevelKey, string>;

export const belongingKeys = [
  "spare_tire",
  "jack",
  "audio",
  "mats",
  "documents",
  "spare_key",
  "first_aid",
] as const;
export type BelongingKey = (typeof belongingKeys)[number];

export const belongingLabels = {
  spare_tire: "زاپاس",
  jack: "جک و آچار چرخ",
  audio: "پخش یا مانیتور",
  mats: "کفپوش",
  documents: "کارت و مدارک خودرو",
  spare_key: "کلید یدک",
  first_aid: "جعبه کمک‌های اولیه و مثلث خطر",
} satisfies Record<BelongingKey, string>;

/** Body zones of the damage map, seen from above with the front at the top */
export const damageZoneKeys = [
  "front_bumper",
  "hood",
  "front_left_fender",
  "front_right_fender",
  "windshield",
  "front_left_door",
  "front_right_door",
  "roof",
  "rear_left_door",
  "rear_right_door",
  "rear_left_quarter",
  "rear_right_quarter",
  "trunk",
  "rear_bumper",
] as const;
export type DamageZoneKey = (typeof damageZoneKeys)[number];

export const damageZoneLabels = {
  front_bumper: "سپر جلو",
  hood: "کاپوت",
  front_left_fender: "گلگیر جلو چپ",
  front_right_fender: "گلگیر جلو راست",
  windshield: "شیشه جلو",
  front_left_door: "در جلو چپ",
  front_right_door: "در جلو راست",
  roof: "سقف",
  rear_left_door: "در عقب چپ",
  rear_right_door: "در عقب راست",
  rear_left_quarter: "گلگیر عقب چپ",
  rear_right_quarter: "گلگیر عقب راست",
  trunk: "در صندوق",
  rear_bumper: "سپر عقب",
} satisfies Record<DamageZoneKey, string>;

export const intakeSchema = z.object({
  fuel: z.enum(fuelLevelKeys).nullable().default(null),
  belongings: z.array(z.enum(belongingKeys)).max(belongingKeys.length).default([]),
  /** valuables left in the car, in the customer's words */
  valuables: z.string().trim().max(300).default(""),
  damage: z
    .array(z.object({ zone: z.enum(damageZoneKeys), note: z.string().trim().max(120).default("") }))
    .max(damageZoneKeys.length)
    .default([])
    .refine(
      (marks) => new Set(marks.map((mark) => mark.zone)).size === marks.length,
      "هر ناحیه بدنه فقط یک بار علامت می‌خورد.",
    ),
  /** toman the workshop may spend without calling the customer; null = call for everything */
  costCap: z.number().int().min(0).max(1_000_000_000_000).nullable().default(null),
  /** who handed the vehicle over, when not the owner */
  handedOverBy: z.string().trim().max(80).default(""),
  /** the customer signs the printed sheet instead of the pad */
  signedOnPaper: z.boolean().default(false),
});
export type Intake = z.infer<typeof intakeSchema>;

/** A stored intake; rows admitted before the checklist existed read as empty */
export const storedIntakeSchema = intakeSchema.catch(() => intakeSchema.parse({}));

// ---------------------------------------------------------------------------
// Estimate decisions
// ---------------------------------------------------------------------------

export const estimateChannelKeys = ["phone", "in_person", "sms", "panel"] as const;
export type EstimateChannelKey = (typeof estimateChannelKeys)[number];

export const estimateChannelLabels = {
  phone: "تلفنی",
  in_person: "حضوری",
  sms: "پیامک",
  panel: "پنل مشتری",
} satisfies Record<EstimateChannelKey, string>;

/** Channels a staff member records a decision from; «panel» is the customer's own */
export const staffEstimateChannels = [
  "phone",
  "in_person",
  "sms",
] as const satisfies readonly EstimateChannelKey[];

/** The coarse steps the customer follows on the repair tracker */
export const repairTrackerSteps = [
  "admitted",
  "awaiting_part",
  "in_repair",
  "ready",
  "delivered",
] as const satisfies readonly WorkOrderStatusKey[];

/** Where a detailed status sits on the customer's coarse tracker */
export const trackerStepFor = (status: WorkOrderStatusKey): (typeof repairTrackerSteps)[number] => {
  if (status === "awaiting_review" || status === "awaiting_customer") return "admitted";
  // declined: the vehicle waits for its owner just like a finished one
  if (status === "repair_done" || status === "declined") return "ready";
  return status;
};

export const workOrderLineKinds = ["service", "part"] as const;
export type WorkOrderLineKind = (typeof workOrderLineKinds)[number];

export const workOrderLineKindLabels = {
  service: "اجرت و خدمت",
  part: "قطعه",
} satisfies Record<WorkOrderLineKind, string>;

export const workOrderLineSchema = z.object({
  kind: z.enum(workOrderLineKinds),
  title: z.string().trim().min(2).max(120),
  quantity: z.number().int().min(1).max(999),
  /** unit amount, whole toman */
  amount: z.number().int().min(0).max(1_000_000_000_000),
});
export type WorkOrderLineInput = z.infer<typeof workOrderLineSchema>;

/** A line's money: the real price, and the share the warranty pays (0-100 %, default none) */
type PricedLine = { quantity: number; amount: number; warrantyPercent?: number };

/** What the warranty pays of a line, whole toman */
export const warrantyShare = (line: PricedLine) =>
  Math.round((line.quantity * line.amount * (line.warrantyPercent ?? 0)) / 100);

/** What the customer pays of a line: its price less the warranty's share */
export const customerShare = (line: PricedLine) =>
  line.quantity * line.amount - warrantyShare(line);

/** Invoice total of a work order: what the customer pays over all lines */
export const workOrderTotal = (lines: PricedLine[]) =>
  lines.reduce((sum, line) => sum + customerShare(line), 0);

export const warrantyPercentSchema = z.number().int().min(0).max(100);

/** «گارانتی» for a fully covered line, «۵۰٪ گارانتی» for a shared one, "" otherwise */
export const warrantyTag = (percent: number) =>
  percent >= 100 ? "گارانتی" : percent > 0 ? `${percent.toLocaleString("fa-IR")}٪ گارانتی` : "";

type BillLine = { kind: string; title: string; quantity: number; amount: number };

/**
 * The work order's lines as billed: the club tier's labour discount joins as one negative line
 * (that percent of the labour lines, whole toman, rounded down). Computed, never stored as a
 * work order line, so the workshop's total, the panel and the invoice written at delivery agree.
 */
export function billedLines<Line extends BillLine>(
  lines: Line[],
  tier: { label: string; labourDiscountPct: number },
): (Line | BillLine)[] {
  const labour = workOrderTotal(lines.filter((line) => line.kind === "service"));
  const discount = Math.floor((labour * tier.labourDiscountPct) / 100);
  if (discount <= 0) return lines;
  return [
    ...lines,
    {
      kind: "service",
      title: `تخفیف اجرت باشگاه، سطح ${tier.label} (${tier.labourDiscountPct.toLocaleString("fa-IR")}٪)`,
      quantity: 1,
      amount: -discount,
    },
  ];
}

export const warrantyDecisionKeys = ["pending", "covered", "not_covered"] as const;
export type WarrantyDecisionKey = (typeof warrantyDecisionKeys)[number];

export const warrantyDecisionLabels = {
  pending: "در انتظار بررسی",
  covered: "مشمول گارانتی",
  not_covered: "خارج از گارانتی",
} satisfies Record<WarrantyDecisionKey, string>;

export const estimateStatusKeys = ["pending", "approved", "rejected"] as const;
export type EstimateStatusKey = (typeof estimateStatusKeys)[number];

export const estimateStatusLabels = {
  pending: "در انتظار تأیید مشتری",
  approved: "تأیید شده",
  rejected: "رد شده",
} satisfies Record<EstimateStatusKey, string>;

export const costLineSchema = z.object({
  title: z.string().trim().min(2).max(120),
  /** negative for a discount line (billedLines) */
  amount: z.number().int().min(-1_000_000_000_000).max(1_000_000_000_000),
});
export type CostLine = z.infer<typeof costLineSchema>;

/** A line of an issued invoice as stored: an amendment's credit line is negative */
export const invoiceLineSchema = costLineSchema.extend({ amount: z.number().int() });

/** Lines of a Repair Estimate: invoice lines, so approval copies kind and quantity as they are */
export const estimateLinesSchema = z.array(workOrderLineSchema).min(1).max(30);

/**
 * Stored estimate lines; rows written before kind and quantity were asked read as one part each.
 * `declined`: the customer turned this line down while approving the rest (partial approval).
 */
export const storedEstimateLinesSchema = z.array(
  workOrderLineSchema.extend({
    kind: z.enum(workOrderLineKinds).default("part"),
    quantity: z.number().int().min(1).max(999).default(1),
    declined: z.boolean().default(false),
  }),
);

/**
 * Invoice or estimate lines as the customer reads them: «title (n عدد)» at what the customer
 * pays for the line; a warranty-covered line says so («… (گارانتی)»).
 */
export const customerLines = (lines: (PricedLine & { title: string })[]): CostLine[] =>
  lines.map((line) => {
    const tag = warrantyTag(line.warrantyPercent ?? 0);
    const count = line.quantity > 1 ? `${line.quantity} عدد` : "";
    const detail = [count, tag].filter(Boolean).join("، ");
    return {
      title: detail ? `${line.title} (${detail})` : line.title,
      amount: customerShare(line),
    };
  });

// ---------------------------------------------------------------------------
// Warranty claims: what the dealership claims back from the manufacturer or importer
// ---------------------------------------------------------------------------

export const warrantyClaimStatusKeys = ["submitted", "approved", "rejected", "paid"] as const;
export type WarrantyClaimStatusKey = (typeof warrantyClaimStatusKeys)[number];

export const warrantyClaimStatusLabels = {
  submitted: "ارسال شده",
  approved: "تأیید شده",
  rejected: "رد شده",
  paid: "پرداخت شده",
} satisfies Record<WarrantyClaimStatusKey, string>;

/** Where a claim may move: a rejected one can be sent again; a paid one is final */
export const warrantyClaimTransitions = {
  submitted: ["approved", "rejected"],
  approved: ["paid", "submitted"],
  rejected: ["submitted"],
  paid: [],
} as const satisfies Record<WarrantyClaimStatusKey, readonly WarrantyClaimStatusKey[]>;

export const warrantyClaimCan = (from: WarrantyClaimStatusKey, to: WarrantyClaimStatusKey) =>
  warrantyClaimTransitions[from].some((next) => next === to);

export const roadsideKindKeys = ["tow", "battery", "tire", "fuel", "lockout", "other"] as const;
export type RoadsideKindKey = (typeof roadsideKindKeys)[number];

export const roadsideKindLabels = {
  tow: "حمل با خودروبر",
  battery: "باتری و استارت نشدن",
  tire: "پنچرگیری و تعویض لاستیک",
  fuel: "سوخت‌رسانی",
  lockout: "جاماندن کلید داخل خودرو",
  other: "مشکل دیگر",
} satisfies Record<RoadsideKindKey, string>;

export const roadsideStatusKeys = ["submitted", "dispatched", "done", "cancelled"] as const;
export type RoadsideStatusKey = (typeof roadsideStatusKeys)[number];

export const roadsideStatusLabels = {
  submitted: "ثبت شد، در انتظار اعزام",
  dispatched: "امدادگر اعزام شد",
  done: "انجام شد",
  cancelled: "لغو شد",
} satisfies Record<RoadsideStatusKey, string>;

/** Who cancelled a roadside request */
export const roadsideCancelledByKeys = ["customer", "staff"] as const;
export type RoadsideCancelledBy = (typeof roadsideCancelledByKeys)[number];

/** A cancellation needs a short reason, from the customer or the dispatcher */
export const roadsideCancelReasonSchema = z.string().trim().min(3).max(200);

/** A shared phone position: both coordinates or neither, inside the valid ranges */
export const roadsidePositionSchema = z
  .object({ latitude: z.number().min(-90).max(90), longitude: z.number().min(-180).max(180) })
  .nullable()
  .default(null);

/**
 * When the unit should arrive: dispatch time + ETA, only while it is on its way. A finished or
 * cancelled request has no arrival, so the panel never shows a stale «X minutes».
 */
export const roadsideArrivalIso = (row: {
  status: string;
  dispatchedAt: Date | null;
  etaMinutes: number | null;
}) =>
  row.status === "dispatched" && row.dispatchedAt && row.etaMinutes !== null
    ? new Date(row.dispatchedAt.getTime() + row.etaMinutes * 60_000).toISOString()
    : null;

export const partsInquiryStatusKeys = [
  "open",
  "quoted",
  "unavailable",
  "ordered",
  "closed",
  "paid",
  "ready",
  "delivered",
  "refunded",
] as const;
export type PartsInquiryStatusKey = (typeof partsInquiryStatusKeys)[number];
/** Finished inquiries: off the parts desk, in its paged archive */
export const partsInquiryArchivedStatuses: readonly PartsInquiryStatusKey[] = [
  "closed",
  "delivered",
  "refunded",
];

export const partsInquiryStatusLabels = {
  open: "در انتظار بررسی",
  quoted: "قیمت اعلام شد",
  unavailable: "ناموجود",
  ordered: "سفارش داده شد",
  closed: "بسته شده",
  paid: "پرداخت شد",
  ready: "آماده تحویل",
  delivered: "تحویل شد",
  refunded: "وجه بازگردانده شد",
} satisfies Record<PartsInquiryStatusKey, string>;

/**
 * Where an inquiry may move from each status (enforced by the API with a guarded UPDATE).
 * «quoted» is reached only through a quote (price + validity); a repeated move to the same
 * status is a correction (new note, ETA or price), not a transition. «paid» is reached only
 * by a payment settling a valid quote (@car/api/payments: online, a confirmed transfer or the
 * desk); after it the price is fixed and the part goes «ready» (at the pickup branch, or sent by
 * courier) and then «delivered» (final). «refunded» (final) is reached only through the cashier's
 * refund (payments.refundParts) of a paid part that was never handed over.
 */
export const partsInquiryTransitions = {
  open: ["quoted", "unavailable", "ordered", "closed"],
  quoted: ["quoted", "unavailable", "ordered", "closed", "paid"],
  unavailable: ["unavailable", "quoted", "ordered", "closed"],
  ordered: ["ordered", "quoted", "unavailable", "closed"],
  closed: ["open"],
  paid: ["ready", "delivered"],
  ready: ["ready", "delivered"],
  delivered: [],
  refunded: [],
} as const satisfies Record<PartsInquiryStatusKey, readonly PartsInquiryStatusKey[]>;

/** Statuses of an inquiry whose price was paid: a financial record, never removed */
export const partsPaidStatuses = [
  "paid",
  "ready",
  "delivered",
  "refunded",
] as const satisfies readonly PartsInquiryStatusKey[];

export const partsInquiryCan = (from: PartsInquiryStatusKey, to: PartsInquiryStatusKey) =>
  partsInquiryTransitions[from].some((next) => next === to);

/** Statuses an inquiry may move to `status` from */
export const partsInquirySourcesOf = (status: PartsInquiryStatusKey) =>
  partsInquiryStatusKeys.filter((from) => partsInquiryCan(from, status));

/** Customer-facing note of «unavailable» / «ordered» */
export const PARTS_STATUS_NOTE_MAX = 300;

/** How far ahead an «ordered» arrival date may be */
export const PARTS_ETA_MAX_DAYS = 365;

/** How a paid part reaches the customer: picked up at a branch, or sent (fee paid on delivery) */
export const partsDeliveryKeys = ["pickup", "courier"] as const;
export type PartsDeliveryKey = (typeof partsDeliveryKeys)[number];

export const partsDeliveryLabels = {
  pickup: "تحویل در شعبه",
  courier: "ارسال با پیک یا پست (هزینه ارسال هنگام تحویل)",
} satisfies Record<PartsDeliveryKey, string>;

/** How and where a parts order is handed over, as both panels show it */
export const partsDeliveryOf = (
  row: { deliveryMethod: string; deliveryBranchId: string | null; deliveryAddress: string },
  branch: { name: string; address: string } | null,
) => ({
  method: partsDeliveryKeys.find((key) => key === row.deliveryMethod) ?? "pickup",
  branchId: row.deliveryBranchId,
  branchName: branch?.name ?? "",
  branchAddress: branch?.address ?? "",
  /** courier: where to send it */
  address: row.deliveryAddress,
});

/** Statuses in which the customer may still cancel an inquiry (nothing paid yet) */
export const partsCancellableStatuses = [
  "open",
  "quoted",
  "unavailable",
  "ordered",
] as const satisfies readonly PartsInquiryStatusKey[];

/** Grade of the part the customer wants */
export const partsTypeKeys = ["oem", "aftermarket", "approved", "commercial"] as const;
export type PartsTypeKey = (typeof partsTypeKeys)[number];

export const partsTypeLabels = {
  oem: "اصلی (OEM)",
  aftermarket: "افترمارکت",
  approved: "جایگزین مورد تأیید",
  commercial: "تجاری",
} satisfies Record<PartsTypeKey, string>;

/** How many of one part a single inquiry may ask for */
export const PARTS_QUANTITY_MAX = 999;

export const partsAvailabilityKeys = ["in_stock", "order"] as const;
export type PartsAvailabilityKey = (typeof partsAvailabilityKeys)[number];

export const partsAvailabilityLabels = {
  in_stock: "موجود در انبار",
  order: "سفارش از تأمین‌کننده",
} satisfies Record<PartsAvailabilityKey, string>;

/** Loyalty points a paid service invoice earns: one point per ۱۰٬۰۰۰ toman */
export const servicePointsFor = (amount: number, tomanPerPoint = 10_000) =>
  Math.floor(amount / tomanPerPoint);

/** Points for answering the after-service survey */
export const SURVEY_POINTS = 50;

/** Invoice number of a delivered work order: F-<Jalali year>-<8 characters of the order id>.
 *  Not sequential; 8 hex digits keep numbers of one year apart without a counter. */
export const invoiceNumberFor = (jalaliYear: number, orderId: string) =>
  `F-${jalaliYear}-${orderId
    .replace(/^[a-z]+[_-]/i, "")
    .replace(/[^0-9a-z]/gi, "")
    .slice(-8)
    .toUpperCase()
    .padStart(8, "0")}`;

// ---------------------------------------------------------------------------
// Jalali calendar: invoice numbers and monthly reports follow the dealership's
// calendar. The runtime's ICU does the conversion (Bun, Workers and browsers ship it).
// ---------------------------------------------------------------------------

const jalaliParts = new Intl.DateTimeFormat("en-US-u-ca-persian-nu-latn", {
  timeZone: "Asia/Tehran",
  year: "numeric",
  month: "numeric",
  day: "numeric",
});

export type JalaliDate = { year: number; month: number; day: number };

/** The Jalali calendar date of an instant, as seen in Tehran */
export function toJalali(date: Date): JalaliDate {
  const parts = jalaliParts.formatToParts(date);
  const of = (type: "year" | "month" | "day") =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { year: of("year"), month: of("month"), day: of("day") };
}

export const jalaliYear = (date: Date) => toJalali(date).year;

const DAY_MS = 24 * 60 * 60 * 1000;
const jalaliKey = ({ year, month, day }: JalaliDate) => year * 400 + month * 32 + day;

/**
 * The same Jalali day of month `months` months later (or earlier), at the same time of day;
 * a day the target month lacks (31 in Mehr, 30 in a common Esfand) becomes its last day.
 */
export function addJalaliMonths(date: Date, months: number): Date {
  const from = toJalali(date);
  const total = from.year * 12 + from.month - 1 + months;
  const target = jalaliKey({
    year: Math.floor(total / 12),
    month: (total % 12) + 1,
    day: from.day,
  });
  // start from an estimate and walk day by day (Tehran has no DST, so a day is always DAY_MS)
  let at = date.getTime() + Math.round(months * 30.44) * DAY_MS;
  while (jalaliKey(toJalali(new Date(at))) < target) at += DAY_MS;
  while (jalaliKey(toJalali(new Date(at))) > target) at -= DAY_MS;
  return new Date(at);
}

/**
 * Noon in Tehran of a typed Jalali date «1365/04/20» (Latin digits; / - . separators),
 * or null when it is malformed or the day does not exist (e.g. 1403/07/31, 30 Esfand of a common year).
 */
export function parseJalaliDate(text: string): Date | null {
  const [, yearText, monthText, dayText] =
    /^\s*(\d{4})\s*[/.-]\s*(\d{1,2})\s*[/.-]\s*(\d{1,2})\s*$/.exec(text) ?? [];
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (!yearText || year < 1300 || year > 1500 || month < 1 || month > 12 || day < 1 || day > 31)
    return null;
  const target = { year, month, day };
  // 1 Farvardin is 20-21 March; walk from the estimate to the day
  let at = Date.UTC(year + 621, 2, 21, 8, 30) + Math.round((month - 1) * 30.5 + day - 1) * DAY_MS;
  while (jalaliKey(toJalali(new Date(at))) < jalaliKey(target)) at += DAY_MS;
  while (jalaliKey(toJalali(new Date(at))) > jalaliKey(target)) at -= DAY_MS;
  return jalaliKey(toJalali(new Date(at))) === jalaliKey(target) ? new Date(at) : null;
}

const tehranDayFormat = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tehran" });

/** «2026-09-28»: the calendar day of an instant in Tehran; sorts as text */
export const tehranDay = (date: Date) => tehranDayFormat.format(date);

/** Noon in Tehran of a tehranDay() (Iran keeps UTC+03:30 all year since 2022) */
export const tehranNoon = (day: string) => new Date(`${day}T12:00:00+03:30`);

const jalaliLongFormat = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
  timeZone: "Asia/Tehran",
  year: "numeric",
  month: "long",
  day: "numeric",
});

/** «۶ مهر ۱۴۰۵»: a date as the customer reads it in an SMS */
export const jalaliDateLabel = (date: Date) => jalaliLongFormat.format(date);

export const jalaliMonthLabels = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
] as const;

/** Label of a 1-based Jalali month */
export const jalaliMonthLabel = (month: number) => jalaliMonthLabels[month - 1] ?? "";

/** The `count` Jalali months ending with the month of `date`, oldest first */
export function recentJalaliMonths(date: Date, count: number): { year: number; month: number }[] {
  const { year, month } = toJalali(date);
  return Array.from({ length: count }, (_, index) => {
    const offset = month - (count - 1 - index);
    const yearShift = Math.floor((offset - 1) / 12);
    return { year: year + yearShift, month: offset - yearShift * 12 };
  });
}

/**
 * Amounts per Tehran day («2026-10-20», as SQL groups them) summed into each of `months`
 * (recentJalaliMonths), same order; a day outside them is left out
 */
export function monthlyTotals(
  months: readonly { year: number; month: number }[],
  rows: readonly { day: string; amount: number }[],
) {
  const totals = months.map(() => 0);
  for (const row of rows) {
    const { year, month } = toJalali(tehranNoon(row.day));
    const index = months.findIndex((item) => item.year === year && item.month === month);
    if (index >= 0) totals[index] = (totals[index] ?? 0) + row.amount;
  }
  return totals;
}
